import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import { useQuery } from 'react-query';
import {
  getGeneralCategories,
  getGrade,
  getSubject
} from 'src/pages/Assessment/service/assessment.service';
import { useSelector, useDispatch } from 'react-redux';
import {
  setGeneralCategory,
  setSubjectAndGrade
} from '../../../src/redux/slices/tableFilterSlice';
import { RootState } from 'src/redux/store';

const FilterMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [category, setCategory] = useState<'generalCategory' | 'subjectAndGrade'>('generalCategory');
  const [options, SetOptions] = useState<any[]>([]);
  const [selectedGeneralCategory, setSelectedGeneralCategory] = useState<any[]>([]);
  const [selectedSubjectAndGrade, setSelectedSubjectAndGrade] = useState<any[]>([]);

  const dispatch = useDispatch();
  const generalCategory = useSelector((state: RootState) => state.question.generalCategory);
  const subjectAndGrade = useSelector((state: RootState) => state.question.subjectAndGrade);

  const open = Boolean(anchorEl);

  useEffect(() => {
    setSelectedGeneralCategory(generalCategory);
    setSelectedSubjectAndGrade(subjectAndGrade);
  }, [generalCategory, subjectAndGrade]);

  const { data: generalCategoryData } = useQuery<any>(
    ['generalCategory'],
    getGeneralCategories
  );
  const { data: gradeData } = useQuery<any>(['grade'], getGrade);
  const { data: subjectData } = useQuery<any>(['subject'], getSubject);

  useEffect(() => {
    if (generalCategoryData) {
      SetOptions(generalCategoryData);
    }
  }, [generalCategoryData]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCategoryChange = (newCategory: 'generalCategory' | 'subjectAndGrade') => {
    setCategory(newCategory);
    SetOptions([]);

    if (newCategory === 'generalCategory') {
      if (generalCategoryData) SetOptions(generalCategoryData);
    } else {
      if (subjectData && gradeData) {
        SetOptions([...subjectData, ...gradeData]);
      }
    }
  };

  const handleToggle = (option: any) => {
    if (category === 'generalCategory') {
      const currentIndex = selectedGeneralCategory.indexOf(option);
      const newSelected = [...selectedGeneralCategory];
      currentIndex === -1 ? newSelected.push(option) : newSelected.splice(currentIndex, 1);
      setSelectedGeneralCategory(newSelected);
    } else {
      const currentIndex = selectedSubjectAndGrade.indexOf(option);
      const newSelected = [...selectedSubjectAndGrade];
      currentIndex === -1 ? newSelected.push(option) : newSelected.splice(currentIndex, 1);
      setSelectedSubjectAndGrade(newSelected);
    }
  };

  const handleCancel = () => {
    dispatch(setGeneralCategory([]));
    dispatch(setSubjectAndGrade([]));
    setSelectedGeneralCategory([]);
    setSelectedSubjectAndGrade([]);
    handleClose();
  };

  const handleOk = () => {
    dispatch(setGeneralCategory(selectedGeneralCategory));
    dispatch(setSubjectAndGrade(selectedSubjectAndGrade));
    handleClose();
  };

  return (
    <Box display="flex" gap={1}>
      <Badge
        badgeContent={
          (selectedGeneralCategory?.length || 0) +
          (selectedSubjectAndGrade?.length || 0)
        }
        color="primary"
      >
        <IconButton
          onClick={handleClick}
          disableRipple
          disableFocusRipple
          sx={{
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <FilterAltIcon fontSize="small" />
        </IconButton>
      </Badge>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleOk}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 250,
            boxShadow: 3,
            maxWidth: 200,
            maxHeight: 300,
            overflow: 'auto',
            padding: 0,
            margin: 0
          }
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-evenly"
          gap={2}
          px={2}
        >
          <Tooltip title="General Category">
            <IconButton
              onClick={() => handleCategoryChange('generalCategory')}
              size="small"
              sx={{
                backgroundColor:
                  category === 'generalCategory' ? '#380563' : '#f5f5f5',
                color: category === 'generalCategory' ? 'white' : 'black',
                border: '1px solid #ccc',
                '&:hover': {
                  backgroundColor:
                    category === 'generalCategory' ? '#380563' : '#f5f5f5'
                }
              }}
            >
              <BookmarksIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Subject and Grade">
            <IconButton
              onClick={() => handleCategoryChange('subjectAndGrade')}
              size="small"
              sx={{
                backgroundColor:
                  category === 'subjectAndGrade' ? '#380563' : '#f5f5f5',
                color: category === 'subjectAndGrade' ? 'white' : 'black',
                border: '1px solid #ccc',
                '&:hover': {
                  backgroundColor:
                    category === 'subjectAndGrade' ? '#380563' : '#f5f5f5'
                }
              }}
            >
              <MenuBookIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {options.length === 0 ? (
          <Typography variant="body2" sx={{ px: 2, py: 1, color: 'text.disabled' }}>
            No matching filters
          </Typography>
        ) : (
          options.map((option) => (
            <MenuItem
              key={option.id || option.caption}
              onClick={() => handleToggle(option)}
              sx={{
                borderRadius: 1,
                width: '100%',
                paddingY: 0.25,
                paddingX: 0.75,
                minHeight: '28px',
                ':hover': { backgroundColor: '#f0f0f0' }
              }}
            >
              <Checkbox
                checked={
                  category === 'generalCategory'
                    ? selectedGeneralCategory.includes(option)
                    : selectedSubjectAndGrade.includes(option)
                }
                sx={{ padding: 0.05 }}
              />
              <ListItemText
                primary={option.caption}
                sx={{
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  fontSize: '0.75rem'
                }}
              />
            </MenuItem>
          ))
        )}

        <Divider sx={{ my: 1 }} />

        <Box display="flex" justifyContent="space-between" px={2} py={1}>
          <Button
            onClick={handleCancel}
            sx={{ fontSize: '0.900rem', color: 'black' }}
          >
            Clear
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default FilterMenu;
